/**
 * Cloud Cost — every plan's resource rate card, and the marketplace policy.
 *
 * Layer 1 of resource billing (docs/runtime-pricing-cloud-cost-design.md):
 * per plan, each meter carries an included allowance, a prorated overage rate
 * per unit block, and a when-exceeded action. The master switch and observe
 * mode live here too, so the whole model is turned on from one screen.
 */
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import {
  getMarketplaceSettings, getPlanCloudCost, listPlans, updateMarketplaceSettings,
  updatePlanCloudCost,
  type AdminMarketplace, type AdminMeterRow, type AdminPlan,
} from '@/api/billingAdmin'
import { Field, InfoBox, Section, Td, Th, errText, tableStyle } from '../adminUi'

const METER_ORDER = ['users', 'rows', 'db_gb', 'file_gb', 'requests']
const UNIT_HINT: Record<string, string> = {
  users: 'user / month',
  rows: 'rows',
  db_gb: 'GB / month',
  file_gb: 'GB / month',
  requests: 'requests / month',
}

export default function CloudCostPanel() {
  const [plans, setPlans] = useState<AdminPlan[]>([])
  const [planCode, setPlanCode] = useState<string>('')
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR')
  const [meters, setMeters] = useState<Record<string, AdminMeterRow> | null>(null)
  const [market, setMarket] = useState<AdminMarketplace | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const [{ plans: rows }, mkt] = await Promise.all([
          listPlans(), getMarketplaceSettings(),
        ])
        setPlans(rows)
        setMarket(mkt)
        if (rows.length) setPlanCode((c) => c || rows[0].code)
      } catch (e) {
        toast.error(errText(e, 'Could not load the plan catalog'))
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const loadCard = useCallback(async (code: string) => {
    if (!code) return
    try {
      const data = await getPlanCloudCost(code)
      setMeters(data.meters)
    } catch (e) {
      toast.error(errText(e, 'Could not load the rate card'))
    }
  }, [])

  useEffect(() => { void loadCard(planCode) }, [planCode, loadCard])

  const patchMeter = (meter: string, patch: Partial<AdminMeterRow>) =>
    setMeters((m) => (m ? { ...m, [meter]: { ...m[meter], ...patch } } : m))

  const saveCard = async () => {
    if (!meters) return
    setSaving(true)
    try {
      const rows = [...METER_ORDER, 'base_fee'].map((k) => ({
        meter: k,
        allowance_qty: meters[k]?.allowance_qty ?? null,
        unit_block: meters[k]?.unit_block,
        rate_usd_minor: meters[k]?.rate_usd_minor ?? 0,
        rate_inr_minor: meters[k]?.rate_inr_minor ?? 0,
        over_action: meters[k]?.over_action ?? 'charge',
        enabled: meters[k]?.enabled ?? true,
      }))
      const result = await updatePlanCloudCost(planCode, rows)
      setMeters(result.meters)
      toast.success(`Cloud cost saved for ${planCode}`)
    } catch (e) {
      toast.error(errText(e, 'Save failed'))
    } finally {
      setSaving(false)
    }
  }

  const saveMarket = async (patch: Partial<AdminMarketplace>) => {
    try {
      setMarket(await updateMarketplaceSettings(patch))
      toast.success('Marketplace settings saved')
    } catch (e) {
      toast.error(errText(e, 'Save failed'))
    }
  }

  if (loading) return <Loader2 size={18} className="spin" />

  const rateKey = currency === 'INR' ? 'rate_inr_minor' : 'rate_usd_minor'
  const sym = currency === 'INR' ? '₹' : '$'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {market && (
        <Section title="Resource billing">
          <InfoBox>
            The metered model: apps pay monthly from their plan&apos;s rate card, and
            owners resell the same meters to other organizations with a margin.
            While OFF, the legacy one-time seat charges stay in effect. Observe
            mode computes and records every statement but moves no money — run
            one full cycle that way before going live.
          </InfoBox>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              <input
                type="checkbox" checked={market.resource_billing_enabled}
                onChange={(e) => void saveMarket({ resource_billing_enabled: e.target.checked })}
              />
              Resource billing enabled
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              <input
                type="checkbox" checked={market.resource_billing_observe}
                onChange={(e) => void saveMarket({ resource_billing_observe: e.target.checked })}
              />
              Observe only (record, never debit)
            </label>
          </div>
        </Section>
      )}

      <Section title="Plan rate card">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
            {plans.map((p) => (
              <option key={p.code} value={p.code}>{p.display_name} ({p.code})</option>
            ))}
          </select>
          <div style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
            {(['INR', 'USD'] as const).map((c) => (
              <button
                key={c} type="button" onClick={() => setCurrency(c)}
                style={{
                  padding: '5px 14px', fontSize: 13.5, cursor: 'pointer',
                  background: currency === c ? 'var(--color-accent-weak)' : 'transparent',
                  color: currency === c ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                  fontWeight: currency === c ? 600 : 400, border: 'none',
                }}
              >
                {c}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Allowances are shared; only the rates change per currency.
          </span>
        </div>

        {meters && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 14, width: 90 }}>Base fee</span>
              <input
                type="number" min={0} step="0.01" style={{ width: 110 }}
                value={(meters.base_fee?.[rateKey] ?? 0) / 100}
                onChange={(e) => patchMeter('base_fee', {
                  [rateKey]: Math.round((parseFloat(e.target.value) || 0) * 100),
                } as Partial<AdminMeterRow>)}
              />
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {sym} / app / month — 0 = usage-only pricing
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>Meter</Th>
                    <Th>Included allowance</Th>
                    <Th>Overage rate ({sym})</Th>
                    <Th>Per</Th>
                    <Th>When exceeded</Th>
                  </tr>
                </thead>
                <tbody>
                  {METER_ORDER.map((k) => {
                    const row = meters[k]
                    if (!row) return null
                    return (
                      <tr key={k}>
                        <Td style={{ fontWeight: 500 }}>{row.label}</Td>
                        <Td>
                          <input
                            type="number" min={0} style={{ width: 110 }}
                            placeholder="Unlimited"
                            value={row.allowance_qty ?? ''}
                            onChange={(e) => patchMeter(k, {
                              allowance_qty: e.target.value === '' ? null : parseFloat(e.target.value) || 0,
                            })}
                          />
                          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginLeft: 6 }}>
                            blank = unlimited (never charges)
                          </span>
                        </Td>
                        <Td>
                          <input
                            type="number" min={0} step="0.01" style={{ width: 100 }}
                            disabled={row.over_action === 'block'}
                            value={(row[rateKey] ?? 0) / 100}
                            onChange={(e) => patchMeter(k, {
                              [rateKey]: Math.round((parseFloat(e.target.value) || 0) * 100),
                            } as Partial<AdminMeterRow>)}
                          />
                        </Td>
                        <Td>
                          <input
                            type="number" min={0} style={{ width: 90 }}
                            value={row.unit_block}
                            onChange={(e) => patchMeter(k, { unit_block: parseFloat(e.target.value) || 1 })}
                          />
                          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginLeft: 6 }}>
                            {UNIT_HINT[k]}
                          </span>
                        </Td>
                        <Td>
                          <select
                            value={row.over_action}
                            onChange={(e) => patchMeter(k, { over_action: e.target.value as AdminMeterRow['over_action'] })}
                          >
                            <option value="charge">Charge overage</option>
                            <option value="block">Block</option>
                            <option value="alert">Alert only</option>
                          </select>
                        </Td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginTop: 12 }}>
              <input
                type="checkbox" checked={meters.users?.enabled ?? true}
                onChange={(e) => patchMeter('users', { enabled: e.target.checked })}
              />
              Charge per runtime user beyond the allowance
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                — off: users over the allowance follow &ldquo;when exceeded&rdquo; but are not priced
              </span>
            </label>

            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => void saveCard()} disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : `Save ${planCode} rate card`}
            </button>
          </>
        )}
      </Section>

      {market && (
        <Section title="Owner reselling (marketplace)">
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', maxWidth: 620 }}>
            <Field label="Platform fee on owner revenue (%)"
                   hint="Taken on each monthly settlement.">
              <input
                type="number" min={0} max={100} step="0.5" style={{ width: '100%' }}
                defaultValue={market.platform_fee_percent}
                onBlur={(e) => void saveMarket({ platform_fee_percent: parseFloat(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Default margin suggestion (%)"
                   hint="Pre-fills owner price sheets as plan cost × (1 + margin).">
              <input
                type="number" min={0} step="1" style={{ width: '100%' }}
                defaultValue={market.default_margin_percent}
                onBlur={(e) => void saveMarket({ default_margin_percent: parseFloat(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Owner rate ceiling (× platform rate)"
                   hint="0 = no ceiling.">
              <input
                type="number" min={0} step="0.5" style={{ width: '100%' }}
                defaultValue={market.owner_rate_ceiling_multiplier}
                onBlur={(e) => void saveMarket({ owner_rate_ceiling_multiplier: parseFloat(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Price-change notice (days)"
                   hint="Before a new price sheet binds existing organizations.">
              <input
                type="number" min={0} step="1" style={{ width: '100%' }}
                defaultValue={market.price_change_notice_days}
                onBlur={(e) => void saveMarket({ price_change_notice_days: parseInt(e.target.value, 10) || 0 })}
              />
            </Field>
          </div>
        </Section>
      )}
    </div>
  )
}
